import React, { useState, useEffect, useRef } from "react";
import "./header.css";
import logo from "../logo.png";
import Paper from "@mui/material/Paper";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import Grid from "@mui/material/Grid";
import Badge from "@mui/material/Badge";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import { Toolbar, Typography, Box, CircularProgress } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Drawer, IconButton, List, ListItem, ListItemText, Button, AppBar, Menu, Divider, TextField, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import FixFilter from './fixFilter';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux'; // Import Redux hooks
import { fetchCart, fetchWishlist, addToWishlist, removeFromWishlist, fetchProfileImage, setProfileImage } from './store'; // Import your action
import { useSnackbar } from 'notistack';
import Avatar from '@mui/material/Avatar';
import { Remove } from "@mui/icons-material";

const NewNotes = () => {
  const [language, setLanguage] = useState("");
  const [currency, setCurrency] = useState("");
  const [country, setCountry] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [itemImages, setItemImages] = useState({});
  const [value, setValue] = React.useState("1");
  const [userName, setUserName] = useState("");
  const dispatch = useDispatch();
  const cartState = useSelector(state => state.cart);
  const navigate = useNavigate();

  const searchRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const wishlistState = useSelector(state => state.wishlist);
  const wishlistItems = wishlistState.items;
  const wishlistCount = wishlistState.totalItems;
  const { enqueueSnackbar } = useSnackbar();

  const userFromStore = useSelector((state) => state.user);
  const { url: profileImageUrl } = useSelector((state) => state.profileImage);

  // State to store authenticated user data
  const [currentUser, setCurrentUser] = useState(null);

  // Add state to prevent multiple auth checks
  const authCheckRef = useRef(false);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    dispatch(fetchWishlist());
  }, [dispatch]);

  useEffect(() => {
    // Only check auth once when component mounts
    if (!authCheckRef.current) {
      checkAuthToken();
      authCheckRef.current = true;
    }

    // Fetch profile image if user exists
    if (currentUser) {
      dispatch(fetchProfileImage());
    }
  }, [dispatch, currentUser]);

  const checkAuthToken = async () => {
    const token = localStorage.getItem("authToken");

    if (token) {
      try {
        const response = await fetch(
          "https://car.amar.co.jp/api/authorization/user/get/user/details",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setCurrentUser(data.userDetails);
        } else {
          // If token is invalid, clear it
          localStorage.removeItem("authToken");
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        // Don't clear user on network errors
        // This avoids logout on temporary connection issues
      }
    }
  };

  // Function to get initials from name
  const getInitials = (fullName) => {
    if (!fullName) return "";
    const parts = fullName.trim().split(" ");
    return parts.length > 1
      ? parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
      : parts[0][0].toUpperCase();
  };

  const handleAddToWishlist = async (modelId) => {
    const result = await dispatch(addToWishlist(modelId));
    if (result.success) {
      enqueueSnackbar("Added to wishlist successfully", { variant: 'success' });
    } else if (result.alreadyInWishlist) {
      enqueueSnackbar("This item is already in your wishlist", { variant: 'info' });
    } else {
      enqueueSnackbar(result.message || "Failed to add to wishlist", { variant: 'error' });
    }
  };

  const handleRemoveFromWishlist = async (modelId) => {
    const result = await dispatch(removeFromWishlist(modelId));
    if (result.success) {
      enqueueSnackbar("Removed from wishlist successfully", { variant: 'success' });
    } else {
      enqueueSnackbar(result.message || "Failed to remove from wishlist", { variant: 'error' });
    }
  };

  // Function to handle search submission and navigate to search results page
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const query = searchQuery.trim();

    if (query === "") {
      return;
    }

    // Store the search query in localStorage for use on the search results page
    localStorage.setItem("searchQuery", query);
    navigate("/globalSearch");
  };

  // Debounce search to prevent too many API calls
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim() !== "") {
        handleSearch();
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.trim() === "") {
      return;
    }

    try {
      setIsSearching(true);
      const token = localStorage.getItem("authToken");

      // Start by trying the model search endpoint
      const modelResponse = await fetch(
        `https://car.amar.co.jp/api/dashboard/global/model/search/${encodeURIComponent(searchQuery)}?page=0&size=10`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        }
      );

      const modelData = await modelResponse.json();

      // If we found models, use those results
      if (modelResponse.ok && modelData.cars && modelData.cars.length > 0) {
        setSearchResults(modelData.cars);
        setShowSearchResults(true);
        localStorage.setItem("searchResults", JSON.stringify(modelData.cars));
      }
      // If no model results found or error, try the global search endpoint
      else {
        const globalResponse = await fetch(
          `https://car.amar.co.jp/api/dashboard/global/search/filter?inputType=${encodeURIComponent(searchQuery)}&page=0&size=10`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        const globalData = await globalResponse.json();

        if (globalResponse.ok && globalData.status === 200) {
          // Map the global search results to the same format as model results for consistency
          const formattedResults = globalData.data.map(item => ({
            carModelId: item.carModelId,
            carBrandName: item.carBrandName,
            carModelName: item.carModelName,
            carPrice: item.carPrice,
            carFuel: item.categoriesName || null,
            carManufactureDate: item.year ? `${item.year}-01` : null,
            carDoors: null,
            // Add any other needed fields
          }));

          setSearchResults(formattedResults);
          setShowSearchResults(true);
          localStorage.setItem("searchResults", JSON.stringify(formattedResults));
        } else {
          console.error("Failed to fetch search results from both endpoints");
          setSearchResults([]);
        }
      }
    } catch (error) {
      console.error("Error performing search:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Global search functionality
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value.trim() === "") {
      setShowSearchResults(false);
    } else {
      setShowSearchResults(true);
    }
  };

  // Navigate to car details page when a search result is clicked
  const handleSearchResultClick = (carModelId) => {
    setShowSearchResults(false);
    navigate(`/product/${carModelId}`);
  };

  // Use cart items and count from Redux state
  useEffect(() => {
    setCartItems(cartState.items);
    setCartCount(cartState.totalItems);
  }, [cartState]);

  const tabHandleChangess = (event, newValue) => {
    setValue(newValue);
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerOpen(open);
  };

  // Close drawer if all fields are filled
  const handleCloseIfComplete = () => {
    if (language && currency && country) {
      setDrawerOpen(false);
    }
  };

  const handleChangeLanguage = (event) => {
    setLanguage(event.target.value);
    handleCloseIfComplete();
  };

  const handleChangeCurrency = (event) => {
    setCurrency(event.target.value);
    handleCloseIfComplete();
  };

  const handleChangeCountry = (event) => {
    setCountry(event.target.value);
    handleCloseIfComplete();
  };

  //////////////////////////////// cart drawer
  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawerCart = (open) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setIsOpen(open);
    if (open) {
      fetchCartData();
    }
  };

  // Function to fetch cart item images
  const fetchItemImage = async (modelId) => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No authentication token found");
        return null;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${modelId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        // Create a blob URL from the image data
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      } else {
        console.error(`Failed to fetch image for model ${modelId}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching image for model ${modelId}:`, error);
      return null;
    }
  };

  const fetchCartData = async () => {
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        console.error("No authentication token found. Please log in.");
        return;
      }

      const response = await fetch(
        "https://car.amar.co.jp/api/master/car-cart-and-whishlist/get/items?type=ADD_IN_CART",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (data.code === 200) {
        setCartItems(data.items);
        setCartCount(data.totalItems);

        // Fetch images for each cart item
        const imagePromises = data.items.map(async (item) => {
          const imageUrl = await fetchItemImage(item.modelId);
          return { modelId: item.modelId, imageUrl };
        });

        const imagesResults = await Promise.all(imagePromises);

        // Convert array of results to an object with modelId as keys
        const imagesObject = imagesResults.reduce((acc, curr) => {
          if (curr.imageUrl) {
            acc[curr.modelId] = curr.imageUrl;
          }
          return acc;
        }, {});

        setItemImages(imagesObject);

      } else {
        console.error("Failed to fetch cart data:", data.message);
      }
    } catch (error) {
      console.error("Error fetching cart data:", error);
    }
  };

  useEffect(() => {
    fetchCartData();
  }, []);

  // My account ______________________________
  const [anchorEl, setAnchorEl] = useState(null);
  // Using userState for local user data from API
  const [userState, setUserState] = useState(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Fetch user details on component mount (or after login)
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("authToken"); // Retrieve token from localStorage

        if (!token) {
          console.error("No authentication token found. Please log in.");
          return;
        }

        const response = await fetch(
          "https://car.amar.co.jp/api/authorization/user/get/user/details",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // Use dynamic token
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUserState(data.userDetails); // Store user details in state
        } else {
          console.error("Failed to fetch user details");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    fetchUserDetails();
  }, []);

  // Handle Logout Function
  const handleLogout = () => {
    localStorage.removeItem("authToken"); // Remove auth token
    setUserState(null); // Reset user state
    navigate("/login"); // Redirect to login page
  };

  // Handle opening the menu on hover
  const handleMouseEnter = (event) => {
    clearTimeout(timeoutRef.current);
    setAnchorEl(event.currentTarget);
  };

  // Handle menu mouse enter to prevent closing
  const handleMenuMouseEnter = () => {
    clearTimeout(timeoutRef.current);
  };

  // Handle mouse leave with delay to allow moving to menu
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100); // Short delay to allow moving cursor to menu
  };

  // Handle Click (Redirect to Login Page if not logged in)
  const handleLoginClick = () => {
    if (!userState) {
      navigate("/login");
    }
    // If logged in, just show the dropdown menu - no redirect
  };

  // Function to handle profile navigation
  const handleProfileNavigation = () => {
    // Close the menu
    setAnchorEl(null);
    if (!currentUser) {
      navigate("/MyAccount");
    }
    // Navigate to myAccount page

  };

  // Cart List delete___________________________________________
  const shippingFee = 32.21;
  const total = cartItems.reduce((sum, item) => sum + item.price, 0) + shippingFee;
  const removeItem = (itemId) => {
    setCartItems((prevItems) => {
      const updatedCart = prevItems.filter((item) => item.modelId !== itemId);
      return updatedCart.length > 0 ? updatedCart : [];
    });
  };

  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedItem) {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No authentication token found. Please log in.");
          return;
        }

        const response = await fetch(
          `https://car.amar.co.jp/api/master/car-cart-and-whishlist/delete/item?Enter%20ModelId=${selectedItem.modelId}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          // Remove item from local state
          setCartItems((prevItems) => {
            const updatedCart = prevItems.filter((item) => item.modelId !== selectedItem.modelId);
            return updatedCart;
          });

          // Update the cart count - decrement by 1
          setCartCount((prevCount) => Math.max(0, prevCount - 1));

          // Also remove the image from itemImages state
          setItemImages((prevImages) => {
            const updatedImages = { ...prevImages };
            delete updatedImages[selectedItem.modelId];
            return updatedImages;
          });
        } else {
          console.error("Failed to delete item from the server.");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
      }
    }
    setSelectedItem(null);
    setOpenDialog(false);
  };

  return (
    <div className="main-container">
      {/* First Container __________________________________________ */}
      <Box
        sx={{
          // p: 2,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
        style={{ borderBottom: "1px solid #E6E6E6" }}
      >
        <Grid container alignItems="center" justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>
          {/* Left Section: Logo */}
          <Grid
            item
            xs={6}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <PlayCircleFilledWhiteOutlinedIcon sx={{ fontSize: "2rem" }} />
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: "bold",
              }}
            >
              Contact Us
            </Typography>
          </Grid>

          {/* Right Section: Dropdowns for Desktop */}
          <Grid
            item
            md={6}
            sx={{
              display: { xs: "none", md: "flex" }, // Ensure visible on md and larger
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            {/* Language Dropdown */}
            <Toolbar disableGutters sx={{ p: 0 }}>
              <Typography className="content-type" sx={{ mr: 1, fontWeight: "bold" }}>
                LANGUAGES
              </Typography>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  sx={{
                    boxShadow: "none",
                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  }}
                  value={language}
                  onChange={handleChangeLanguage}
                  displayEmpty
                >
                  <MenuItem value={10}>Hindi</MenuItem>
                  <MenuItem value={20}>English</MenuItem>
                  <MenuItem value={30}>Japanese</MenuItem>
                </Select>
              </FormControl>
            </Toolbar>

            {/* Currency Dropdown */}
            <Toolbar disableGutters sx={{ p: 0 }}>
              <Typography className="content-type" sx={{ mr: 1, fontWeight: "bold" }}>
                CURRENCY
              </Typography>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  sx={{
                    boxShadow: "none",
                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  }}
                  value={currency}
                  onChange={handleChangeCurrency}
                  displayEmpty
                >
                  <MenuItem value={10}>USD</MenuItem>
                  <MenuItem value={20}>INR</MenuItem>
                  <MenuItem value={30}>EUR</MenuItem>
                </Select>
              </FormControl>
            </Toolbar>

            {/* Country Dropdown */}
            <Toolbar disableGutters sx={{ p: 0 }}>
              <Typography className="content-type" sx={{ mr: 1, fontWeight: "bold" }}>
                COUNTRY
              </Typography>
              <FormControl sx={{ minWidth: 120 }}>
                <Select
                  sx={{
                    boxShadow: "none",
                    ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  }}
                  value={country}
                  onChange={handleChangeCountry}
                  displayEmpty
                >
                  <MenuItem value={10}>India</MenuItem>
                  <MenuItem value={20}>Japan</MenuItem>
                  <MenuItem value={30}>United States</MenuItem>
                </Select>
              </FormControl>
            </Toolbar>
          </Grid>

          {/* Right Section: Hamburger Menu for Mobile */}
          <Grid
            item
            xs={6}
            sx={{
              display: { xs: "flex", md: "none" }, // Only visible on xs
              justifyContent: "flex-end",
            }}
          >
            <IconButton onClick={toggleDrawer(true)}>
              <MenuIcon sx={{ fontSize: "2rem" }} />
            </IconButton>
          </Grid>
        </Grid>

        {/* Drawer for Hamburger Menu */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          sx={{ display: { xs: "block", md: "none" } }}
        >
          <Box
            sx={{
              width: 250,
              p: 2,
            }}
            role="presentation"
          >
            <List>
              <ListItem>
                <ListItemText primary="LANGUAGES" />
                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    sx={{
                      boxShadow: "none",
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                    }}
                    value={language}
                    onChange={handleChangeLanguage}
                    displayEmpty
                  >
                    <MenuItem value={10}>Hindi</MenuItem>
                    <MenuItem value={20}>English</MenuItem>
                    <MenuItem value={30}>Japanese</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>

              <ListItem>
                <ListItemText primary="CURRENCY" />
                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    sx={{
                      boxShadow: "none",
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                    }}
                    value={currency}
                    onChange={handleChangeCurrency}
                    displayEmpty
                  >
                    <MenuItem value={10}>USD</MenuItem>
                    <MenuItem value={20}>INR</MenuItem>
                    <MenuItem value={30}>EUR</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>

              <ListItem>
                <ListItemText primary="COUNTRY" />
                <FormControl sx={{ minWidth: 120 }}>
                  <Select
                    sx={{
                      boxShadow: "none",
                      ".MuiOutlinedInput-notchedOutline": { border: 0 },
                    }}
                    value={country}
                    onChange={handleChangeCountry}
                    displayEmpty
                  >
                    <MenuItem value={10}>India</MenuItem>
                    <MenuItem value={20}>Japan</MenuItem>
                    <MenuItem value={30}>United States</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </Box>

      {/* Second Container _____________________________________________  */}
      <div className="header">
        <Box sx={{ flexGrow: 1, px: { xs: 2, sm: 3 } }}>
          <Grid container alignItems="center" spacing={2}>
            {/* Logo */}
            <Grid
              item
              xs={12}
              sm={4}
              md={2}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", sm: "flex-start" },
                gap: 2,
              }}
            >
              <img
                src={logo}
                alt="Logo"
                className="logo"
                style={{
                  backgroundColor: "white",
                  width: "100%",
                  maxWidth: "191px",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              />
            </Grid>

            {/* Search Bar */}
            <Grid item xs={12} sm={8} md={7}>
              <div ref={searchRef} style={{ position: "relative", width: "100%" }}>
                <Paper
                  component="form"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #f2f2f2",
                    borderRadius: "10px",
                  }}
                  onSubmit={handleSearchSubmit}
                >
                  <InputBase
                    sx={{ ml: 1, flex: 1, paddingLeft: "10px" }}
                    placeholder="Find New And Used Car (brand, model, body type, year...)"
                    inputProps={{ "aria-label": "search cars" }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <IconButton
                    type="submit"
                    sx={{
                      p: "13px",
                      backgroundColor: "red",
                      borderRadius: "1px 10px 10px 1px",
                      "&:hover": { background: "red" },
                    }}
                    aria-label="search"
                    disabled={isSearching}
                  >
                    {isSearching ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      <SearchIcon
                        className="search-button"
                        style={{
                          width: "83px",
                          borderRadius: "0px",
                          color: "white",
                        }}
                      />
                    )}
                  </IconButton>
                </Paper>

                {/* Search Results Dropdown */}
                {showSearchResults && searchResults.length > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1400,
                      backgroundColor: "white",
                      boxShadow: "0px 8px 16px rgba(0,0,0,0.1)",
                      borderRadius: "0 0 10px 10px",
                      maxHeight: "350px",
                      overflowY: "auto",
                    }}
                  >
                    <List>
                      {searchResults.map((item) => (
                        <ListItem
                          key={item.carModelId}
                          button
                          onClick={() => handleSearchResultClick(item.carModelId)}
                          sx={{
                            "&:hover": { backgroundColor: "#f5f5f5" },
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <Box sx={{ width: "100%" }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {item.carBrandName} {item.carModelName}
                            </Typography>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2" color="text.secondary">
                                {item.carFuel || "N/A"} • {item.carManufactureDate?.split('-')[0] || "N/A"}
                              </Typography>
                              <Typography variant="body2" color="error" fontWeight="bold">
                                ${item.carPrice?.toLocaleString() || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* No Results Message */}
                {showSearchResults && searchQuery !== "" && searchResults.length === 0 && !isSearching && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      zIndex: 1400,
                      backgroundColor: "white",
                      boxShadow: "0px 8px 16px rgba(0,0,0,0.1)",
                      borderRadius: "0 0 10px 10px",
                      padding: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography>No results found for "{searchQuery}"</Typography>
                  </Box>
                )}
              </div>
            </Grid>

            {/* Icons Section */}
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: "flex",
                justifyContent: {
                  xs: "space-between",
                  md: "space-around",
                },
                gap: 2,
                mt: { xs: 2, md: 0 },
              }}
            >
              <span
                className="header-icons"
                onClick={() => navigate("/wishlist")}
                // onClick={handleAddToWishlist}
                style={{ cursor: "pointer" }}
              >
                <Badge badgeContent={wishlistCount} color="warning">
                  <FavoriteIcon className="heart-icon" />
                </Badge>
                <span className="text-option" style={{ marginTop: "4px" }}>
                  FAVOURITE
                </span>
              </span>

              <span>
                <IconButton
                  onClick={toggleDrawerCart(true)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Badge badgeContent={cartCount} color="error">
                    <LocalMallOutlinedIcon sx={{ color: "#333" }} />
                  </Badge>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 500,
                      letterSpacing: "0.01em",
                      color: "#333",
                      marginTop: "4px",
                      textTransform: "uppercase",
                    }}
                  >
                    CART
                  </Typography>
                </IconButton>

                <Drawer anchor="right" open={isOpen} onClose={toggleDrawerCart(false)}>
                  <Box sx={{ width: 450, height: "100vh", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ padding: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        My Shopping Cart ({cartCount})
                      </Typography>
                      <IconButton onClick={toggleDrawerCart(false)}>
                        <CloseIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, overflowY: "auto", padding: 2 }}>
                      {cartItems.length > 0 ? (
                        <List>
                          {cartItems.map((item) => (
                            <ListItem key={item.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              {/* <img src={images[images.imageUrl1] || "placeholder.jpg"} alt={item.name} width={80} height={50} style={{ borderRadius: 8 }} /> */}

                              {/* Use the fetched image from API or fall back to placeholder */}

                              <img
                                src={itemImages[item.modelId] || "/placeholder.jpg"}
                                alt={item.modelName}
                                width={80}
                                height={50}
                                style={{
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  backgroundColor: !itemImages[item.modelId] ? "#f0f0f0" : "transparent"
                                }}
                              />

                              <Box sx={{ flex: 1, ml: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>{`${item.brand} ${item.modelName}`}</Typography>
                                {/* <Typography variant="caption" sx={{ color: "gray" }}>${item.categoriesName}</Typography> */}
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Typography variant="body1" color="error">${item.price}</Typography>
                                  {/* <Typography variant="body2" sx={{ textDecoration: "line-through", color: "gray" }}>
                                    ${item.originalPrice}
                                  </Typography> */}
                                </Box>
                              </Box>
                              <IconButton color="error" onClick={() => handleDeleteClick(item)}>
                                <DeleteIcon />
                              </IconButton>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                          <ShoppingBagOutlinedIcon sx={{ fontSize: 50, color: "red", marginBottom: 2 }} />
                          <Typography variant="h6" sx={{ fontWeight: "bold" }}>Your Cart is Empty</Typography>
                          <Typography variant="body2" sx={{ color: "gray" }}>You have no items in your shopping cart yet.</Typography>
                        </Box>
                      )}
                    </Box>

                    {cartItems.length > 0 && (
                      <Box sx={{ padding: 2, borderTop: "1px solid #ddd", backgroundColor: "white", position: "sticky", bottom: 0 }}>
                        <Box display="flex" gap={2}>
                          <TextField label="Coupon Code" variant="outlined" fullWidth size="medium" />
                          <Button variant="contained" sx={{ backgroundColor: "white", color: "black", height: "56px" }}>Apply</Button>
                        </Box>
                        <Divider sx={{ my: 2 }} />

                        <Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography>Shipping Fee</Typography>
                            <Typography>${shippingFee.toFixed(2)}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between" mt={1}>
                            <Typography variant="h6">Total</Typography>
                            <Typography variant="h6" color="error">${total.toFixed(2)}</Typography>
                          </Box>
                        </Box>

                        <Button variant="contained" color="error" sx={{ width: "100%", mt: 2 }} onClick={() => navigate("/delivery")} >Buy Now</Button>
                        <Button variant="contained" color="error" onClick={toggleDrawerCart(false)} sx={{ width: "100%", mt: 1 }}>Close</Button>
                      </Box>
                    )}
                  </Box>
                </Drawer>

                {/* Delete Confirmation Dialog */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                  <DialogTitle>Confirm Deletion</DialogTitle>
                  <DialogContent>
                    <DialogContentText>Are you sure you want to delete this item from your cart?</DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setOpenDialog(false)} color="primary">Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error">Delete</Button>
                  </DialogActions>
                </Dialog>
              </span>

              {/* My Account _______________________________________________________________________________________________________________________________________________________________________________ */}
              <span
                className="header-icons"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleLoginClick}
                style={{ cursor: "pointer", position: "relative" }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    width: 60,
                    py: 1,
                    borderRadius: 2,
                    transition: 'background-color 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                  onClick={(e) => {
                    if (currentUser) {
                      e.stopPropagation(); // Prevent the menu from opening
                      navigate("/myAccount");
                    } else {
                      // If user is logged out, just let the event bubble up to show the login menu
                    }
                  }}
                >
                  {currentUser ? (

                    <>
                      <Avatar
                        src={profileImageUrl}
                        alt={currentUser.firstName}
                        sx={{ width: 34, height: 34, mt: -1 }}
                        onError={(e) => {
                          console.error("Header image failed to load");
                          e.target.src = ""; // Clear src to show the fallback
                        }}
                      >
                        {getInitials(`${currentUser.firstName} ${currentUser.lastName || ''}`)}
                      </Avatar>
                      <span className="text-option">{currentUser.firstName}</span>
                    </>
                  ) : (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer" }}>
                        <img
                          src="image/Profile.svg"
                          alt="login"
                          style={{ marginBottom: -2, width: 30, height: 30, marginTop: -5 }}
                        />
                        <span
                          className="text-option"
                          style={{ color: "black" }}
                        >
                          LOGIN
                        </span>
                      </div>
                    </>
                  )}
                </Box>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  MenuListProps={{ onMouseEnter: handleMenuMouseEnter, onMouseLeave: handleMouseLeave }}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  sx={{ mt: 1, cursor: "pointer" }}
                >
                  {!currentUser ? (
                    <Box sx={{ p: 2, width: 230 }}>
                      {/* Menu content for logged out users */}
                      <Typography variant="body2">
                        <strong>New Customer?</strong>{" "}
                        <span
                          style={{ color: "#e60000", cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent event bubbling
                            setAnchorEl(null); // Close the menu
                            navigate("/signup");
                          }}
                        >
                          Sign Up
                        </span>
                      </Typography>
                      <MenuItem onClick={() => {
                        setAnchorEl(null); // Close the menu
                        navigate("/about-us");
                      }}>About Us</MenuItem>
                      <MenuItem onClick={() => {
                        setAnchorEl(null); // Close the menu
                        navigate("/contact-us");
                      }}>Contact Us</MenuItem>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, width: 250, }}>
                      {/* Profile picture in menu for logged in users */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer', }} onClick={() => navigate("/myAccount")}>
                        {profileImageUrl ? (
                          <Avatar
                            src={profileImageUrl}
                            alt={currentUser.firstName}
                            sx={{ width: 40, height: 40, mr: 1 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 40, height: 40, mr: 1, bgcolor: '#000000' }}>
                            {getInitials(`${currentUser.firstName} ${currentUser.lastName || ''}`)}
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body2">
                            <strong>{currentUser.firstName} {currentUser.lastName || ''}</strong>
                          </Typography>
                          <Typography variant="body2" color="gray">{currentUser.email}</Typography>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      {/* Fixed menu items with proper navigation */}
                      <MenuItem onClick={() => {
                        setAnchorEl(null); // Close the menu
                        navigate("/myProfile");
                      }}>
                        <PermIdentityIcon sx={{ mr: 2, color: "#e60000" }} />
                        My Profile
                      </MenuItem>
                      <MenuItem onClick={() => {
                        setAnchorEl(null);
                        navigate("/orderHistory");
                      }}>
                        <LocalMallOutlinedIcon sx={{ mr: 2, color: "#e60000" }} />
                        Order History
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null); // Close the menu
                          navigate("/inquiryHistory");
                        }}
                      >
                        <img
                          src="image/invoice.svg"
                          alt="inquiry"
                          style={{ width: 24, height: 24, marginRight: 16 }}
                        />
                        Inquiry History
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null); // Close the menu
                          navigate("/wishlist");
                        }}
                      >
                        <img
                          src="image/heart.svg"
                          alt="wishlist"
                          style={{ width: 24, height: 24, marginRight: 16 }}
                        />
                        My WishList
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setAnchorEl(null); // Close the menu
                          navigate("/myAddress");
                        }}
                      >
                        <img
                          src="image/address.svg"
                          alt="Address Icon"
                          style={{ width: 24, height: 24, marginRight: 16 }}
                        />
                        My Address
                      </MenuItem>
                      <Divider sx={{ my: 1 }} />
                      <MenuItem onClick={() => {
                        setAnchorEl(null); // Close the menu
                        handleLogout();
                      }}>
                        <LogoutOutlinedIcon sx={{ mr: 2, color: "#e60000" }} />
                        Logout
                      </MenuItem>
                    </Box>
                  )}
                </Menu>
              </span>

            </Grid>
          </Grid>
        </Box>
        {isScrolled && (
          <AppBar
            // position={scrolled ? 'fixed' : 'static'}
            // color="secondary"
            sx={{
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1200, // Ensures this header is above the content
              transition: 'top 0.3s ease',
            }}
          >
            <Toolbar>
              {/* <SecondaryHeader /> */}
            </Toolbar>
          </AppBar>
        )}
      </div>
      <div style={{ borderBottom: "1px solid #E6E6E6" }}>
        <Box sx={{ typography: "body1", marginLeft: "17%" }}>
          <TabContext value={value}>
            <Box>
              <TabList
                onChange={tabHandleChangess}
                aria-label="lab API tabs example"
                sx={{ "& .MuiTabs-indicator": { display: "none" } }}
              >
                <Tab label="About us"
                  onClick={() => navigate('/AboutUs')} value="1" />
                <Tab label="Contact" onClick={() => navigate('/contact')} value="4" />
              </TabList>
            </Box>
          </TabContext>
        </Box>
      </div>
    </div>
  )
}

export default NewNotes;